/**
 * Timeline Instance Composable
 * Manages the Vis.js Timeline instance, groups, and items
 */

import {
    ref, nextTick, type Ref
} from 'vue';
import type { TimeFormatPreference } from '@/utils/timeFormat';
import {
    sortTimelineData,
    createGroupsArray,
    parseTimelineItems
} from '../utils/timelineDataTransformers';
import {
    PaginatedData, UserData, TimelineItem, TimelineGroup
} from '../utils/types';
import {
    deriveVisibleSlice,
    getZoomLevels,
    calculateTimelineDateRange,
    getHeaderText,
    getNowForTimezone
} from './timelineHelpers';

interface VisTimeline {
    on: (event: string, callback: (properties?: unknown) => void) => void;
    off: (event: string) => void;
    redraw: () => void;
    destroy: () => void;
    setCurrentTime?: (time: Date) => void;
    [key: string]: unknown;
}

interface VisDataSet {
    get: {
        (id: string | number): unknown;
        (): unknown[];
    };
    update: (item: unknown) => void;
    add: (items: unknown[]) => void;
    remove: (id: string | number) => void;
    clear: () => void;
    on: (event: string, callback: (event: string, properties: unknown) => void) => void;
    [key: string]: unknown;
}

declare global {
    interface Window {
        vis: {
            DataSet: new (data: unknown[]) => VisDataSet;
            Timeline: new (
                container: HTMLElement,
                items: VisDataSet,
                groups: VisDataSet,
                options: TimelineOptions
            ) => VisTimeline;
        };
    }
}

interface TimelineOptions {
    [key: string]: unknown;
}

interface ColumnDisplay {
    userId?: boolean;
    email?: boolean;
}

interface LegendItem {
    name: string;
    color: string;
}

/**
 * Composable for managing the timeline instance and its data
 * @param containerRef - Reference to the timeline container element
 * @param options - Timeline configuration options
 * @param updateDateRange - Function to update the date range in options
 * @param timezone - Optional timezone for converting UTC times
 * @param groupByRef - Grouping strategy reference
 * @param _totalTeamScheduleRef - Total team schedule reference (unused)
 * @param timeFormatPreference - User's time format preference (12h or 24h)
 * @param filterStartDate - Start date from date filter (for empty state)
 * @param filterEndDate - End date from date filter (for empty state)
 * @param dateBoundaries - Optional date boundaries for fallback when no data
 * @param hasViewLeaveTypePermission - Ref for view leave type permission
 * @returns Timeline instance management functions and data
 */
export const useTimelineInstance = (
    containerRef: Ref<HTMLElement | null>,
    options: Ref<TimelineOptions>,
    updateDateRange: (minDate: Date | null, maxDate: Date | null) => void,
    timezone?: Ref<string | undefined>,
    groupByRef?: Ref<string | 'NONE'>,
    _totalTeamScheduleRef?: Ref<number>,
    dateBoundaries?: Ref<{ start: Date; end: Date } | undefined>,
    timeFormatPreference?: Ref<TimeFormatPreference | null>,
    filterStartDate?: Ref<string | undefined>,
    filterEndDate?: Ref<string | undefined>,
    hasViewLeaveTypePermission?: Ref<boolean>
// eslint-disable-next-line max-params
) => {
    const timelineInstance = ref<VisTimeline | null>(null);
    const groups = ref<VisDataSet | null>(null);
    const items = ref<VisDataSet | null>(null);
    const legendItems = ref<LegendItem[]>([]);
    const currentTotalCount = ref<number | undefined>(undefined);
    const collapsedGroups = ref<Set<string | number>>(new Set());

    /**
     * Initializes the timeline with data. Backward compatible: accepts either paginatedData (legacy)
     * or raw full dataset with pagination parameters for internal slicing.
     */
    const getWorkingData = (
        paginatedData: PaginatedData | null,
        groupBy: string,
        optionsOverride?: { rawData?: UserData[]; currentPage?: number; pageSize?: number }
    ): UserData[] | null => {

        if (optionsOverride?.rawData) {
            const fullSorted = sortTimelineData(optionsOverride.rawData, groupBy);
            const page = optionsOverride.currentPage ?? 1;
            const size =
            optionsOverride.pageSize ??
            (options.value as { pageSize?: number }).pageSize ??
            10;

            return deriveVisibleSlice(fullSorted, groupBy, page, size);
        }

        if (paginatedData?.data?.length) {
            return sortTimelineData(paginatedData.data, groupBy);
        }

        return null;
    };

    const updateTimeAxisBasedOnZoom = () => {
        if (!timelineInstance.value) return;

        const range = (timelineInstance.value as unknown as { getWindow: () => { start: Date; end: Date } }).getWindow();
        const scale = range.end.getTime() - range.start.getTime();

        // Determine if 12-hour format should be used
        const is12Hour = timeFormatPreference?.value?.timeFormat === '12h';
        const ZOOM_LEVELS = getZoomLevels(is12Hour);

        const zoomConfig = ZOOM_LEVELS.find((level) => scale >= level.threshold) ?? ZOOM_LEVELS.at(-1);
        if (!zoomConfig) return;

        (timelineInstance.value as unknown as { setOptions: (opts: TimelineOptions) => void }).setOptions({
            timeAxis: zoomConfig.timeAxis,
            format: zoomConfig.format,
            showMajorLabels: true,
            showMinorLabels: true
        });
    };

    /**
     * Helper function to handle group collapse/expand tracking
     */
    const handleGroupToggle = (groupId: string | number) => {
        const group = groups.value?.get(groupId);
        if (group && typeof group === 'object' && 'showNested' in group) {
            // Group is collapsed when showNested is false
            if (group.showNested === false) {
                collapsedGroups.value.add(groupId);
            } else {
                collapsedGroups.value.delete(groupId);
            }
        }
    };

    /**
     * Helper function to process group update events
     */
    const processGroupUpdateEvent = (event: string, properties: unknown) => {
        if (event === 'update' && properties && typeof properties === 'object' && 'items' in properties) {
            const items = (properties as { items: Array<string | number> }).items;
            items.forEach(handleGroupToggle);
        }
    };

    const createTimeline = (
        container: HTMLElement,
        itemsArray: TimelineItem[],
        groupsArray: TimelineGroup[],
        _totalCount: number | undefined
    ) => {
        items.value = new window.vis.DataSet(itemsArray);
        groups.value = new window.vis.DataSet(groupsArray);

        timelineInstance.value = new window.vis.Timeline(
            container,
            items.value,
            groups.value,
            options.value
        );

        // Always add event listeners for header update
        timelineInstance.value.on('rangechanged', () => {
            updateTimeAxisBasedOnZoom();
            if (groupByRef) {
                addGroupHeader(currentTotalCount.value, groupByRef.value);
            }
        });
        timelineInstance.value.on('changed', () => {
            if (groupByRef) {
                addGroupHeader(currentTotalCount.value, groupByRef.value);
            }
        });

        // Track group expansion/collapse for height calculation
        groups.value?.on('*', processGroupUpdateEvent);

        // Initial zoom-based update
        nextTick(() => {
            updateTimeAxisBasedOnZoom();
            timelineInstance.value?.redraw();

            // Fix the current-time (red line) indicator position.
            // vis-timeline's built-in `showCurrentTime` uses new Date() — the raw browser
            // clock — which is in the browser's local timezone (e.g. IST). Timeline items,
            // however, are positioned using wall-clock Dates derived from the selected
            // timezone (same wall-clock digits, no offset). The two coordinate systems are
            // misaligned, so the red line appears at the wrong position.
            //
            // `setCurrentTime(nowForTimezone)` stores offset = nowForTimezone - Date.now()
            // inside vis-timeline. Every subsequent automatic redraw then renders the line
            // at
            //   new Date(Date.now() + offset)
            // which keeps advancing in real time but at the timezone-shifted coordinate,
            // staying aligned with the shifted item positions. Because the timezone watcher
            // in TeamAndScheduleTimeline.vue reinitializes the timeline, this runs again
            // with a fresh offset whenever the user picks a different timezone.
            const instance = timelineInstance.value;
            // Guard against `setCurrentTime` being absent (e.g. a future vis-timeline
            // API change or a test mock without it). Invoke it as a method so the
            // library's internal `this` binding (it reads `this.currentTime`) is kept.
            if (instance && typeof instance.setCurrentTime === 'function') {
                const nowForTimezone = getNowForTimezone(timezone?.value);
                instance.setCurrentTime(nowForTimezone);
            }
        });
    };

    const initializeTimeline = (
        paginatedData: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: {
        rawData?: UserData[];
        currentPage?: number;
        pageSize?: number;
    }
    ): void => {
        const workingData = getWorkingData(paginatedData, groupBy, optionsOverride);
        
        // Create groups array - if empty, create a dummy group to preserve header structure
        const groupsArray = workingData?.length
            ? createGroupsArray(workingData, groupBy, columnDisplay)
            : [
                {
                    id: 'empty-placeholder',
                    content: '',
                    visible: false // Hide the dummy group
                }
            ];

        // When parsing timeline data:
        const { items: itemsArray, legendItems: parsedLegend } = workingData?.length
            ? parseTimelineItems(workingData, timezone?.value, timeFormatPreference?.value, hasViewLeaveTypePermission?.value)
            : { items: [], legendItems: [] };

        legendItems.value = parsedLegend;

        // Use date boundaries as fallback when calculating range
        const fallbackStart = dateBoundaries?.value?.start;
        const fallbackEnd = dateBoundaries?.value?.end;
        
        // Calculate date range for timeline display
        const { minDate, maxDate } = calculateTimelineDateRange(itemsArray, {
            fallbackStart,
            fallbackEnd,
            filterStartDate: filterStartDate?.value,
            filterEndDate: filterEndDate?.value
        });
        updateDateRange(minDate, maxDate);

        const container = containerRef.value;
        if (container) {
            // Always create timeline, even with empty data, to show header
            createTimeline(container, itemsArray, groupsArray, currentTotalCount.value);
            
            // Always add header after timeline creation to show user count
            nextTick(() => {
                if (groupByRef) {
                    addGroupHeader(currentTotalCount.value, groupByRef.value);
                }
            });
        }
    };

    /**
     * Destroys the current timeline instance
     */
    const destroyTimeline = () => {
        if (timelineInstance.value) {
            // Remove event listeners
            timelineInstance.value.off('rangechanged');
            timelineInstance.value.off('changed');

            timelineInstance.value.destroy();
            timelineInstance.value = null;
        }
        // Reset collapsed groups when timeline is destroyed
        collapsedGroups.value.clear();
    };

    /**
     * Reinitializes the timeline (destroys and recreates)
     * @param paginatedData - The paginated data to display
     * @param groupBy - Grouping strategy
     * @param columnDisplay - Configuration for column display
     */
    const reinitializeTimeline = (
        paginatedData: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: {
            rawData?: UserData[];
            currentPage?: number;
            pageSize?: number;
        }
    ): void => {
        destroyTimeline();
        initializeTimeline(paginatedData, groupBy, columnDisplay, optionsOverride);
    };

    /**
     * Updates the timeline groups (useful for column display changes)
     * @param paginatedData - The paginated data
     * @param groupBy - Grouping strategy
     * @param columnDisplay - Configuration for column display
     */
    const updateTimelineGroups = (
        paginatedData: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: {
        rawData?: UserData[];
        currentPage?: number;
        pageSize?: number;
    }
    ): void => {

        const workingData = getWorkingData(paginatedData, groupBy, optionsOverride);
        if (!workingData || !groups.value) return;

        const groupsArray = createGroupsArray(workingData, groupBy, columnDisplay);
        const groupsDs = groups.value; // narrowed non-null after guard
        groupsArray.forEach((group) => {
            if (groupsDs.get(group.id)) {
                groupsDs.update(group);
            }
        });
    };

    /**
     * Checks if the timeline is initialized
     * @returns True if timeline instance exists, false otherwise
     */
    const isInitialized = (): boolean => {
        return timelineInstance.value !== null;
    };

    /**
     * Adds a group header with total count
     * @param totalCount - Total number of users to display in the header
     * @param groupBy - Grouping mode ('TEAMS' or other)
     */
    function addGroupHeader(totalCount?: number, groupBy?: string) {
        if (typeof totalCount === 'number') currentTotalCount.value = totalCount;

        // Remove existing header if present
        document.querySelector('.vis-left-header')?.remove();

        // Find the top panel (the timeline header area with time axis)
        const topPanel = document.querySelector('.vis-panel.vis-top');
        if (!topPanel) return;

        const count = typeof totalCount === 'number' ? totalCount : currentTotalCount.value;
        const headerText = getHeaderText(count, groupBy);
    
        // Check if we have data by looking for labelset with actual groups
        const labelset = document.querySelector('.vis-labelset');
        const hasData = labelset && labelset.children.length > 0;

        // Create header element for BOTH cases (with and without data)
        const headerPanel = document.createElement('div');
        // Use different class based on whether we have data or not
        headerPanel.className = hasData ? 'vis-left-header vis-left-header--with-data' : 'vis-left-header vis-left-header--empty';
        headerPanel.innerHTML = `<div class="vis-left-header-content">${headerText}</div>`;

        // Append to the timeline container (not inside top panel)
        const timelineContainer = topPanel.closest('.vis-timeline');
        if (timelineContainer) {
            timelineContainer.appendChild(headerPanel);
        }
    }

    const zoomIn = () => {
        if (!timelineInstance.value) return;
        const timeline = timelineInstance.value as unknown as {
            zoomIn: (percentage: number) => void;
        };
        timeline.zoomIn(0.2);
    };

    const zoomOut = () => {
        if (!timelineInstance.value) return;
        const timeline = timelineInstance.value as unknown as {
            zoomOut: (percentage: number) => void;
        };
        timeline.zoomOut(0.2);
    };

    /**
     * Helper function to convert item dates to Date objects
     */
    const convertToDate = (date: Date | string | number): Date => {
        return date instanceof Date ? date : new Date(date);
    };

    /**
     * Helper function to find min/max dates from items
     */
    const findDateBounds = (items: TimelineItem[]): { minDate: Date | null; maxDate: Date | null } => {
        let minDate: Date | null = null;
        let maxDate: Date | null = null;
        
        items.forEach((item) => {
            const startDate = convertToDate(item.start);
            const endDate = item.end ? convertToDate(item.end) : startDate;
            
            if (!minDate || startDate < minDate) {
                minDate = startDate;
            }
            if (!maxDate || endDate > maxDate) {
                maxDate = endDate;
            }
        });

        return { minDate, maxDate };
    };

    /**
     * Helper function to set the timeline window
     */
    const setTimelineWindow = (start: Date, end: Date) => {
        if (!timelineInstance.value) return;
        const timeline = timelineInstance.value as unknown as {
            setWindow: (start: Date, end: Date, options?: { animation: boolean }) => void;
        };
        timeline.setWindow(start, end, { animation: true });
    };

    /**
     * Helper function to set fallback window from options
     */
    const setFallbackWindow = () => {
        const min = options.value.min as Date | undefined;
        const max = options.value.max as Date | undefined;
        
        if (min && max) {
            setTimelineWindow(min, max);
        }
    };

    const resetToFit = () => {
        if (!timelineInstance.value || !items.value) return;
        
        const allItems = items.value.get() as TimelineItem[];
        
        if (!allItems || allItems.length === 0) {
            setFallbackWindow();
            return;
        }
        
        const { minDate, maxDate } = findDateBounds(allItems);
        
        if (minDate && maxDate) {
            setTimelineWindow(minDate, maxDate);
        }
    };

    return {
        timelineInstance,
        groups,
        items,
        legendItems,
        collapsedGroups,
        initializeTimeline,
        destroyTimeline,
        reinitializeTimeline,
        updateTimelineGroups,
        isInitialized,
        updateGroupHeader: addGroupHeader,
        zoomIn,
        zoomOut,
        resetToFit
    };
};
